import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useParams, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { debounce } from 'throttle-debounce'
import { ExclamationCircleIcon, EmojiHappyIcon, LinkIcon, DuplicateIcon } from '@heroicons/react/solid'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import DatePicker from 'react-datepicker'
import { format } from 'date-fns-tz'

import TicketTypeForm from '@/pages/teachers/TicketTypeForm'
import WorkoutImage from '@/pages/teachers/WorkoutImage'
import FroalaWrapper from '@/shared/FroalaWrapper'
import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import { randomToastSuccess, toastError } from '@/shared/Toast'
import HostForm from '@/pages/teachers/HostForm'
import Tippy from '@tippyjs/react'
import ToolTip from '@/shared/ToolTip'
import Loading from '@/shared/Loading'
import Card from '@/shared/Card'
import PrimaryButton from '@/shared/Buttons/Primary'
import DefaultButton from '@/shared/Buttons/Default'

const eventDefaults = {
  timezone: 'America/New_York',
  capacity: 1000,
  status: 'draft',
  cohortId: 0,
  recapHoursAfter: 0,
  duration: 60,
  startTime: '07:00',
  shouldCreateCccProject: false,
  ampm: 'PM'
}

const startTimes = [ '00:00', '00:30', '01:00', '01:30', '02:00', '02:30', '03:00', '03:30', '04:00', '04:30',
  '05:00', '05:30', '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '12:00', '12:30' ]

const WorkoutEdit = ({initWorkout}) => {
  const [workout, setWorkout] = useState(initWorkout)
  const [attendees, setAttendees] = useState([])
  const [canDelete, setCanDelete] = useState(false)
  const { putpostRequest, getRequest } = useQuery()
  const isComponentMounted = useRef(false)

  useEffect(() => {
    if (workout.id) {
      getRequest(`/api/v1/teachers/workouts/${workout.id}/edit`, {}, (err, jsonData) => {
        if (err) { /* handled in hook */ return }
        setWorkout(jsonData.workout)
        setAttendees(jsonData.attendees)
        setCanDelete(true)
      })
    } else {
      setEvent(eventDefaults)
    }
    isComponentMounted.current = true
  },[])

  if (!isComponentMounted.current) {
    return <Loading full={true} />
  }

  return <>
    <TheForm theWorkout={workout} canDelete={canDelete} />
    { attendees.length > 0 && 
      <div className='p-6 mt-4 col-span-3 bg-gray-50 rounded-lg shadow-sm border border-gray-200 mt-6'>
        <h3 className="text-lg font-medium text-gray-700 mb-4">Attendees</h3>
        <div className='max-w-5xl'>
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Email Addresses</h4>
            <div className="text-sm text-gray-500 flex flex-wrap gap-1">
              {attendees.map((att, idx) => (
                <span key={idx} className="px-2 py-1 bg-white rounded border border-gray-200">{att.email}</span>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-600 mb-2">Detailed Information</h4>
            <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
              <div className="grid grid-cols-3 bg-gray-50 px-4 py-2 border-b border-gray-200 text-xs font-medium text-gray-600">
                <div>Email</div>
                <div>Name</div>
                <div>Ticket Type</div>
              </div>
              <div className="divide-y divide-gray-100">
                {attendees.map((att, idx) => (
                  <div key={idx} className="grid grid-cols-3 px-4 py-3 text-sm">
                    <div className="text-gray-600 truncate">{att.email}</div>
                    <div className="text-gray-800">{att.displayName}</div>
                    <div className="text-gray-600">{att.ticketTypeName}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    }
  </>
}


const TheForm = ({theWorkout, canDelete}) => {
  const [ currentUser ] = useGlobalState('currentUser')
  const [workout, setWorkout] = useState(theWorkout)
  const [, setToast] = useGlobalState('toast')
  const [confirmDelete, setConfirmDelete] = useState(false)
  let history = useHistory()
  const [timezones, setTimezones] = useState([])
  const [startDate, setStartDate] = useState(workout.id ? new Date(workout.startDate) : new Date())
  const [sendRecapDate, setSendRecapDate] = useState(workout.id ? new Date(workout.sendRecapDate) : new Date())
  const { putpostRequest, getRequest } = useQuery()
  const { formState, reset, register, unregister, handleSubmit, setValue, watch, getValues, setError } = useForm({
   defaultValues: {
      name: workout.name,
      timezone: workout.timezone,
      start_date: workout.startDate || new Date(),
      status: workout.status,
      start_time: workout.startTime,
      description: workout.description,
      instructions: workout.instructions,
      ampm: workout.ampm
    }
  })

  const { isDirty, errors } = formState
  const [loading, setLoading] = useState(false)
  const description = getValues().description
  const instructions = getValues().instructions
  const shortDescription = getValues().short_description
  const summary = getValues().summary
  const recap = getValues().recap

  useEffect(() => {
    register('start_date', { required: true })
    register('description', { required: true })
    register('instructions', { required: true })
    register('short_description')
    register('summary')
    register('recap')
    return () => {
      unregister('start_date')
      unregister('description')
      unregister('instructions')
      unregister('short_description')
      unregister('recap')
    }
  }, [register])

  useEffect(() => {
    setValue('start_date', format(startDate, 'yyyy-MM-dd'), { shouldDirty: true, shouldValidate: true })
  }, [startDate])

  useEffect(() => {
    getRequest('/api/v1/static/timezones', {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }

      if (jsonData.timezones) {
        setTimezones(jsonData.timezones)
        setValue('timezone', workout.timezone, { shouldDirty: false, shouldValidate: false })
      }
    })
  }, [])

  const handleUpdateFroala = (key, val) => {
    setValue(key, val, { shouldDirty: true, shouldValidate: true })
  }

  const onSubmit = debounce(300, data => {
    setLoading(true)

    putpostRequest(`/api/v1/teachers/workouts/${workout.id}`, 'PATCH', { workout: data }, (err, jsonData) => {
      setLoading(false)
      console.log(err)
      if (err) { // 422 code
        if (typeof err !== 'string') { // 500 code on API
          Object.entries(err).forEach(([key, value]) => {
            setError(key, value)
          })
        }
        setToast(toastError("Errors. See below"))
        return
      }

      setWorkout({ ...jsonData.workout })

      setToast(randomToastSuccess())
    })
  })

  const showCopiedNotification = () => {
    setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
      <p className="text-sm font-medium dark:text-green-300 text-green-600">Copied</p>
      <p className="mt-1 text-sm dark:text-green-300 text-green-400">You did it. It's copied. You can paste it anywhere you want now.</p>
    </div>)
  }

  const destroy = () => {
    setLoading(true)
    putpostRequest(`/api/v1/teachers/workouts/${workout.id}`, 'DELETE', {}, (err, jsonData) => {
      setLoading(false)
      if (err) { // 422 code
        if (typeof err !== 'string') { // 500 code on API
          Object.entries(err).forEach(([key, value]) => {
            setError(key, value)
          })
        } else {
          setToast(toastError(err))
        }
        return
      }

      window.location.href = `/dashboard`
    })
  }

  console.log(errors)

  return (
    <form className='mt-6'>
      <div className='sticky top-0 z-10 bg-white dark:bg-gray-800 py-3 border-b border-gray-200 dark:border-gray-700 mb-6'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center'>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {workout.id ? 'Edit Workout' : 'Create Workout'}
          </h1>
          <PrimaryButton onClick={handleSubmit(onSubmit)} loading={loading} text="Save" />
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-3 gap-6">

            <div className='col-span-2'>
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-200 flex flex-col">
                  <span>Status</span>
                  <div className='mt-2 text-xs text-gray-400 dark:text-gray-500'>Workouts are not visible to the public until they are 'published'</div>
                </label>
                <select
                  id="status"
                  {...register('status', { required: true })}
                  className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-cccpurple focus:border-cccpurple sm:text-sm"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="finished">Finished</option>
                </select>
              </div>
            </div>

            <div className="col-span-3 sm:col-span-2">
              <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                <span>Name</span>

                <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                  <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                </Tippy>
              </label>
              <div className="mt-1 flex flex-col rounded-md shadow-sm">
                <div className='relative'>
                  <input type="text" className={errors.name ? 'errors' : ''} {...register('name', { required: true }) } defaultValue={workout.name} placeholder="The health benefits of using a bidet" />
                  { errors.name && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                  </div> }
                </div>
              </div>
              { errors.name && <div className='mt-2 text-sm text-red-600 flex items-center'><ExclamationCircleIcon className="h-4 w-4 mr-1 text-red-500" aria-hidden="true" /> This field is required.</div> }
            </div>

            <div className='col-span-1'>
              {/* Empty column for layout */}
            </div>

            <div className="col-span-3">
              <div className="bg-gray-50 dark:bg-gray-750 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Capacity</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col">
                    <label htmlFor="async_session_member_price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span>Capacity Live Session</span>
                    </label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                      <input 
                        type="number" 
                        id="async_session_member_price"
                        className={errors.capacity_live ? 
                            'block w-full rounded-md border-red-300 pl-7 pr-12 focus:border-red-500 focus:ring-red-500 sm:text-sm' : 
                            'block w-full rounded-md border-gray-300 pl-7 pr-12 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                        } 
                        {...register('capacity_live', { required: true })} 
                        defaultValue={workout.capacity_live} 
                        placeholder="10" 
                      />
                    </div>
                    {errors.capacity_live && <p className="mt-2 text-sm text-red-600">This field is required</p>}
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="async_session_nonmember_price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span>Capacity Written Feedback</span>
                    </label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                      <input 
                        type="number" 
                        id="async_session_nonmember_price"
                        className={errors.capacity_async ? 
                            'block w-full rounded-md border-red-300 pl-7 pr-12 focus:border-red-500 focus:ring-red-500 sm:text-sm' : 
                            'block w-full rounded-md border-gray-300 pl-7 pr-12 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                        } 
                        {...register('capacity_async', { required: true })} 
                        defaultValue={workout.capacity_async} 
                        placeholder="50" 
                      />
                    </div>
                    {errors.capacity_async && <p className="mt-2 text-sm text-red-600">This field is required</p>}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-3">
              <div className="bg-gray-50 dark:bg-gray-750 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-2">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Pricing Options</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col">
                    <label htmlFor="live_session_member_price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span>Member Live Session Price (USD)</span>
                    </label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input 
                        type="number" 
                        id="live_session_member_price"
                        className={errors.live_session_member_price ? 
                            'block w-full rounded-md border-red-300 pl-7 pr-12 focus:border-red-500 focus:ring-red-500 sm:text-sm' : 
                            'block w-full rounded-md border-gray-300 pl-7 pr-12 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                        } 
                        {...register('live_session_member_price', { required: true })} 
                        defaultValue={workout.live_session_member_price} 
                        placeholder="10" 
                      />
                    </div>
                    {errors.live_session_member_price && <p className="mt-2 text-sm text-red-600">This field is required</p>}
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="live_session_nonmember_price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span>Non-Member Live Session Price (USD)</span>
                    </label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input 
                        type="number" 
                        id="live_session_nonmember_price"
                        className={errors.live_session_nonmember_price ? 
                            'block w-full rounded-md border-red-300 pl-7 pr-12 focus:border-red-500 focus:ring-red-500 sm:text-sm' : 
                            'block w-full rounded-md border-gray-300 pl-7 pr-12 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                        } 
                        {...register('live_session_nonmember_price', { required: true })} 
                        defaultValue={workout.live_session_nonmember_price} 
                        placeholder="20" 
                      />
                    </div>
                    {errors.live_session_nonmember_price && <p className="mt-2 text-sm text-red-600">This field is required</p>}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-3">
              <div className="bg-gray-50 dark:bg-gray-750 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Written Feedback Pricing</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col">
                    <label htmlFor="async_session_member_price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span>Member Written Feedback Price (USD)</span>
                    </label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input 
                        type="number" 
                        id="async_session_member_price"
                        className={errors.async_session_member_price ? 
                            'block w-full rounded-md border-red-300 pl-7 pr-12 focus:border-red-500 focus:ring-red-500 sm:text-sm' : 
                            'block w-full rounded-md border-gray-300 pl-7 pr-12 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                        } 
                        {...register('async_session_member_price', { required: true })} 
                        defaultValue={workout.async_session_member_price} 
                        placeholder="10" 
                      />
                    </div>
                    {errors.async_session_member_price && <p className="mt-2 text-sm text-red-600">This field is required</p>}
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="async_session_nonmember_price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span>Non-Member Written Feedback Price (USD)</span>
                    </label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input 
                        type="number" 
                        id="async_session_nonmember_price"
                        className={errors.async_session_nonmember_price ? 
                            'block w-full rounded-md border-red-300 pl-7 pr-12 focus:border-red-500 focus:ring-red-500 sm:text-sm' : 
                            'block w-full rounded-md border-gray-300 pl-7 pr-12 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                        } 
                        {...register('async_session_nonmember_price', { required: true })} 
                        defaultValue={workout.async_session_nonmember_price} 
                        placeholder="20" 
                      />
                    </div>
                    {errors.async_session_nonmember_price && <p className="mt-2 text-sm text-red-600">This field is required</p>}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-3 sm:col-span-3">
              <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                <span>Full Description for the Registration Page</span>

                <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                  <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                </Tippy>
              </label>
              <div className="mt-1 flex flex-1 mx-auto">
                <FroalaWrapper
                  hideButtons={false}
                  showCharacterCounter={false}
                  model={description}
                  updateModel={(val) => handleUpdateFroala('description', val)}
                />
              </div>
              { errors.description && <div className='mt-2 text-sm text-red-600'>This field is required.</div> }
            </div>

            <div className="col-span-3 sm:col-span-3">
              <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                <span>Instructions that are emailed for Written Feedback (and will appear on the 'My Workouts' page)</span>

                <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                  <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                </Tippy>
              </label>
              <div className="mt-1 flex flex-1 mx-auto">
                <FroalaWrapper
                  hideButtons={false}
                  showCharacterCounter={false}
                  model={instructions}
                  updateModel={(val) => handleUpdateFroala('instructions', val)}
                />
              </div>
              { errors.instructions && <div className='mt-2 text-sm text-red-600'>This field is required.</div> }
            </div>

            <div className="col-span-3">
            </div>

            <div className="col-span-3 sm:col-span-1">
              <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                <span>Start Date</span>

                <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                  <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                </Tippy>
              </label>
              <div className="mt-1 flex flex-col rounded-md shadow-sm">
                <div className='relative'>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={new Date().setMonth(startDate.getMonth() + 1)}
                    minDate={new Date()}
                    nextMonthButtonLabel=">"
                    previousMonthButtonLabel="<"
                  />
                  { errors.startsAt && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                  </div> }
                </div>
              </div>
              { errors.startsAt && <div className='mt-2 text-sm text-red-600'>This field is required.</div> }
            </div>

            <div className='col-span-1'>
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-200 flex justify-between">
                  <span>Start Time</span>
                  <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                    <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                  </Tippy>
                </label>
                <select
                  {...register('start_time', { required: true })}
                  className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-cccpurple focus:border-cccpurple sm:text-sm"
                >
                  { startTimes.map((time) => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className='col-span-1'>
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-200 flex justify-between">
                  <span>AM/PM</span>
                  <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                    <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                  </Tippy>
                </label>
                <select
                  {...register('ampm', { required: true })}
                  className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-cccpurple focus:border-cccpurple sm:text-sm"
                >
                  <option>AM</option>
                  <option>PM</option>
                </select>
              </div>
            </div>
            <div className='col-span-1'>
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-200 flex justify-between">
                  <span>Timezone</span>
                  <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                    <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                  </Tippy>
                </label>
                <select
                  {...register('timezone', { required: true })}
                  className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-cccpurple focus:border-cccpurple sm:text-sm"
                >
                  { timezones.map((tz) => (
                    <option key={tz.computer} value={tz.computer}>{tz.human.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-span-3 sm:col-span-1">
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                <span>Duration in Minutes</span>

                <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                  <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                </Tippy>
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input 
                  type="number" 
                  id="duration"
                  className={errors.duration ? 
                    'block w-full rounded-md border-red-300 focus:border-red-500 focus:ring-red-500 sm:text-sm' : 
                    'block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                  } 
                  {...register('duration', { required: true })} 
                  defaultValue={workout.duration} 
                />
                {errors.duration && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                </div>}
              </div>
              {errors.duration && <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1 text-red-500" aria-hidden="true" /> 
                This field is required.
              </p>}
            </div>

            <div className="col-span-3"> </div>

            <div className="col-span-3 sm:col-span-2">
              <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                <span>Zoom URL (or any meeting URL)</span>
              </label>
              <span className='dark:text-gray-300'>Attendees will be redirected to this link when the workout starts.</span>

              <div className="mt-1 flex flex-col rounded-md shadow-sm">
                <div className='relative'>
                  <div className='text-xs text-gray-400 dark:text-gray-500'>Use a custom Zoom/Meeting URL</div>
                  <input 
                    type="text" 
                    className={errors.zoomUrl ? 
                      'block w-full rounded-md border-red-300 focus:border-red-500 focus:ring-red-500 sm:text-sm' : 
                      'block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                    } 
                    {...register('zoom_url')} 
                    defaultValue={workout.zoomUrl} 
                    placeholder="https://www.zoom.us/..." 
                  />
                  { errors.zoom_url && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                  </div> }
                </div>
                { errors.zoom_url && <div className='mt-2 text-sm text-red-600 flex items-center'>
                  <ExclamationCircleIcon className="h-4 w-4 mr-1 text-red-500" aria-hidden="true" />
                  This field is required if publishing.
                </div> }
              </div>
            </div>

            <div className="col-span-3 sm:col-span-2">
              <label htmlFor="submission_url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                <span>Submission URL</span>
              </label>
              <div className="mt-1 flex flex-col rounded-md shadow-sm">
                <div className='relative'>
                  <div className='text-xs text-gray-400 dark:text-gray-500'>URL where participants can submit their work</div>
                  <input 
                    type="url" 
                    id="submission_url"
                    className={errors.submission_url ? 
                      'block w-full rounded-md border-red-300 focus:border-red-500 focus:ring-red-500 sm:text-sm' : 
                      'block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                    } 
                    {...register('submission_url')} 
                    defaultValue={workout.submissionUrl} 
                    placeholder="https://forms.example.com/..." 
                  />
                  { errors.submission_url && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                  </div> }
                </div>
                { errors.submission_url && <div className='mt-2 text-sm text-red-600 flex items-center'>
                  <ExclamationCircleIcon className="h-4 w-4 mr-1 text-red-500" aria-hidden="true" />
                  Please enter a valid URL.
                </div> }
              </div>
            </div>

            <div className="col-span-3 sm:col-span-2">
              <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                <span>Image</span>
              </label>
              <WorkoutImage workout={workout} />
            </div>
          </div>
        </div>
      </div>

      <div className='mt-6 py-4 flex justify-between items-center border-t border-gray-200 dark:border-gray-700'>
        <div>
          {canDelete && !confirmDelete && 
          <button 
            type="button"
            onClick={() => setConfirmDelete(true)} 
            className='inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-red-700 dark:text-red-200 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
          >
            Delete Workout
          </button>
          }
          {confirmDelete && 
            <button 
              type="button"
              onClick={destroy} 
              className='inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
            >
              Confirm Delete
            </button>
          }
        </div>

        {canDelete && !confirmDelete && <div onClick={() => setConfirmDelete(true)} className='text-red-500 text-sm cursor-pointer'>Delete</div> }
        {confirmDelete && <div onClick={destroy} className='text-red-500 text-sm cursor-pointer'>Really delete?</div> }
        <PrimaryButton onClick={handleSubmit(onSubmit)} loading={loading} text="Save Workout" />
      </div>
    </form>
  )
}

export default WorkoutEdit
